import { useSyncExternalStore } from "react";

const DEFAULT_TICK_MS = 30_000;

interface Ticker {
  now: number;
  subscribe: (listener: () => void) => () => void;
}

// One interval per tick length for every subscriber, running only while one is mounted.
const tickers = new Map<number, Ticker>();

const tickerFor = (everyMs: number): Ticker => {
  const existing = tickers.get(everyMs);
  if (existing) return existing;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  const listeners = new Set<() => void>();
  const ticker: Ticker = {
    now: Date.now(),
    subscribe: (listener) => {
      listeners.add(listener);
      if (intervalId === null) {
        ticker.now = Date.now();
        intervalId = setInterval(() => {
          ticker.now = Date.now();
          listeners.forEach((l) => l());
        }, everyMs);
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };
    },
  };
  tickers.set(everyMs, ticker);
  return ticker;
};

const idle = () => () => {};

/** Reads 0 and holds no interval while `active` is false. */
export const useNow = (everyMs = DEFAULT_TICK_MS, active = true): number => {
  const ticker = tickerFor(everyMs);
  return useSyncExternalStore(active ? ticker.subscribe : idle, () =>
    active ? ticker.now : 0,
  );
};
